(() => {
  const hoverPointer = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealSelector = '[data-lgm-reveal]';
  const heroVideoSelector = '[data-lgm-hero-video]';
  const heroVideoPauseKey = 'lgmHeroVideoPaused';
  const pendingHeroVideoHydration = new WeakMap();
  const heroVideoPlayRequests = new WeakMap();

  const finishReveal = (target) => {
    target.classList.remove('lgm-reveal-play');
    target.classList.add('is-visible');
  };

  const playReveal = (target) => {
    if (target.classList.contains('is-visible')) return;
    target.classList.add('lgm-reveal-play');
    const handleAnimationEnd = (event) => {
      if (event.target !== target) return;
      target.removeEventListener('animationend', handleAnimationEnd);
      finishReveal(target);
    };
    target.addEventListener('animationend', handleAnimationEnd);
  };

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              playReveal(entry.target);
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px 12% 0px', threshold: 0 }
        )
      : null;

  const prepareRevealTargets = (root = document) => {
    const targets = [];
    if (root instanceof Element && root.matches(revealSelector)) targets.push(root);
    targets.push(...root.querySelectorAll(revealSelector));

    targets.forEach((target) => {
      if (target.classList.contains('is-visible') || target.classList.contains('lgm-reveal-play')) return;
      if (reducedMotion.matches || !revealObserver) {
        finishReveal(target);
        return;
      }

      const bounds = target.getBoundingClientRect();
      if (bounds.bottom <= 0 || bounds.top <= window.innerHeight) {
        finishReveal(target);
        return;
      }

      revealObserver.observe(target);
    });
  };

  const hydrateHoverImage = (card) => {
    if (!hoverPointer.matches || !card) return;
    const template = card.querySelector('template[data-lgm-hover-image]');
    if (!template) {
      card.classList.add('is-lgm-hover-ready');
      return;
    }

    template.replaceWith(template.content.cloneNode(true));
    requestAnimationFrame(() => card.classList.add('is-lgm-hover-ready'));
  };

  const hydrateFromEvent = (event) => {
    hydrateHoverImage(event.target.closest('.product-card-wrapper, .legemeaux-product-card'));
  };

  const connectionSavesData = () => Boolean(navigator.connection?.saveData);

  const heroVideoCanPlay = () => !reducedMotion.matches && !connectionSavesData();

  const heroVideoWasPaused = () => {
    try {
      return window.sessionStorage?.getItem(heroVideoPauseKey) === 'true';
    } catch {
      return false;
    }
  };

  const rememberHeroVideoPause = (isPaused) => {
    try {
      if (isPaused) window.sessionStorage?.setItem(heroVideoPauseKey, 'true');
      else window.sessionStorage?.removeItem(heroVideoPauseKey);
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  };

  const getHeroVideoControl = (video) =>
    video.closest('.legemeaux-hero')?.querySelector('[data-lgm-hero-video-control]');

  const syncHeroVideoControl = (video) => {
    const control = getHeroVideoControl(video);
    if (!control) return;

    const isPaused = video.paused;
    control.classList.toggle('is-paused', isPaused);
    control.setAttribute('aria-label', isPaused ? control.dataset.playLabel : control.dataset.pauseLabel);
  };

  const concealHeroVideo = (video) => {
    heroVideoPlayRequests.set(video, (heroVideoPlayRequests.get(video) || 0) + 1);
    video.pause();
    video.classList.remove('is-ready');
    delete video.dataset.lgmPlaybackBlocked;
    const control = getHeroVideoControl(video);
    if (!control) return;
    if (document.activeElement === control) {
      video
        .closest('.legemeaux-hero')
        ?.querySelector('.legemeaux-hero__button--primary, .legemeaux-hero__button--secondary')
        ?.focus();
    }
    control.hidden = true;
  };

  const offerHeroVideoPlayback = (video) => {
    video.classList.remove('is-ready');
    video.dataset.lgmPlaybackBlocked = 'true';
    const control = getHeroVideoControl(video);
    if (!control) return;
    control.hidden = false;
    control.classList.add('is-paused');
    control.setAttribute('aria-label', control.dataset.playLabel);
  };

  const playHeroVideo = (video) => {
    const requestId = (heroVideoPlayRequests.get(video) || 0) + 1;
    heroVideoPlayRequests.set(video, requestId);
    const handlePlaybackRefusal = () => {
      if (heroVideoPlayRequests.get(video) !== requestId || !video.paused) return;
      if (
        !heroVideoCanPlay() ||
        document.hidden ||
        video.dataset.lgmInView === 'false' ||
        video.dataset.lgmDestroyed === 'true' ||
        video.isConnected === false
      ) {
        return;
      }
      offerHeroVideoPlayback(video);
    };
    let playback;
    try {
      playback = video.play();
    } catch {
      handlePlaybackRefusal();
      return;
    }
    playback?.catch(handlePlaybackRefusal);
  };

  const bindHeroVideo = (video) => {
    if (video.dataset.lgmHeroBound === 'true') return;
    video.dataset.lgmHeroBound = 'true';
    video.defaultMuted = true;
    video.muted = true;
    if (heroVideoWasPaused()) video.dataset.lgmUserPaused = 'true';

    const control = getHeroVideoControl(video);
    video.addEventListener('playing', () => {
      delete video.dataset.lgmPlaybackBlocked;
      video.classList.add('is-ready');
      if (control) control.hidden = false;
      syncHeroVideoControl(video);
    });
    video.addEventListener('pause', () => syncHeroVideoControl(video));
    video.addEventListener('error', () => concealHeroVideo(video));

    control?.addEventListener('click', () => {
      if (video.paused) {
        delete video.dataset.lgmUserPaused;
        rememberHeroVideoPause(false);
        updateHeroVideo(video);
        return;
      }

      video.dataset.lgmUserPaused = 'true';
      rememberHeroVideoPause(true);
      video.pause();
    });
  };

  const updateHeroVideo = (video) => {
    if (video.dataset.lgmDestroyed === 'true' || video.isConnected === false) return;
    bindHeroVideo(video);

    if (!heroVideoCanPlay()) {
      concealHeroVideo(video);
      return;
    }

    if (video.dataset.lgmCanHydrate !== 'true') return;

    if (document.hidden || video.dataset.lgmInView === 'false') {
      video.pause();
      return;
    }

    let source = video.querySelector('source[src]');
    if (video.dataset.lgmUserPaused === 'true') {
      video.pause();
      if (source) video.classList.add('is-ready');
      const control = getHeroVideoControl(video);
      if (control) control.hidden = false;
      syncHeroVideoControl(video);
      return;
    }

    if (!source) {
      const sourceTemplate = video.parentElement?.querySelector('template[data-lgm-hero-video-source]');
      if (!sourceTemplate) return;
      video.append(sourceTemplate.content.cloneNode(true));
      source = video.querySelector('source[src]');
      if (!source) return;
      video.load();
    }

    playHeroVideo(video);
  };

  const heroVideoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target.dataset.lgmDestroyed === 'true') return;
              entry.target.dataset.lgmInView = entry.isIntersecting ? 'true' : 'false';
              updateHeroVideo(entry.target);
            });
          },
          { threshold: 0.05 }
        )
      : null;

  const scheduleHeroVideo = (video) => {
    if (video.dataset.lgmHeroScheduled === 'true') return;
    video.dataset.lgmHeroScheduled = 'true';
    delete video.dataset.lgmDestroyed;
    bindHeroVideo(video);
    if (heroVideoObserver) {
      video.dataset.lgmInView = 'false';
      heroVideoObserver.observe(video);
    } else {
      video.dataset.lgmInView = 'true';
    }

    const hydrate = () => {
      pendingHeroVideoHydration.delete(video);
      if (video.dataset.lgmDestroyed === 'true' || video.isConnected === false) return;
      video.dataset.lgmCanHydrate = 'true';
      updateHeroVideo(video);
    };

    if (document.readyState === 'complete') {
      hydrate();
      return;
    }

    pendingHeroVideoHydration.set(video, hydrate);
    window.addEventListener('load', hydrate, { once: true });
  };

  const getHeroVideos = (root = document) => {
    const videos = [];
    if (root instanceof Element && root.matches(heroVideoSelector)) videos.push(root);
    videos.push(...(root.querySelectorAll?.(heroVideoSelector) || []));
    return videos;
  };

  const prepareHeroVideos = (root = document) => {
    getHeroVideos(root).forEach(scheduleHeroVideo);
  };

  const teardownHeroVideo = (video) => {
    video.dataset.lgmDestroyed = 'true';
    const pendingHydration = pendingHeroVideoHydration.get(video);
    if (pendingHydration) {
      window.removeEventListener('load', pendingHydration);
      pendingHeroVideoHydration.delete(video);
    }
    heroVideoObserver?.unobserve(video);
    concealHeroVideo(video);
    const sources = video.querySelectorAll('source');
    sources.forEach((source) => source.remove());
    if (sources.length > 0) video.load();
  };

  const updateAllHeroVideos = () => {
    document.querySelectorAll(heroVideoSelector).forEach(updateHeroVideo);
  };

  document.addEventListener('pointerover', hydrateFromEvent, { passive: true });
  document.addEventListener('focusin', hydrateFromEvent);
  document.addEventListener('shopify:section:load', (event) => {
    prepareRevealTargets(event.target);
    prepareHeroVideos(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    getHeroVideos(event.target).forEach(teardownHeroVideo);
  });
  document.addEventListener('visibilitychange', updateAllHeroVideos);

  reducedMotion.addEventListener?.('change', () => {
    document.querySelectorAll(revealSelector).forEach((target) => {
      if (reducedMotion.matches) {
        revealObserver?.unobserve(target);
        finishReveal(target);
      }
    });
    updateAllHeroVideos();
  });

  prepareRevealTargets();
  prepareHeroVideos();
})();
